import * as THREE from "three";
import type {TerrainAmbient} from "../../Server/DB/mapObjects/@MapObject/ObjModifiers/Mod_TerrainDefinition.js";
import {Rect, Vec3} from "../../Utils/ThreeJS/VectorStructs.js";
import {resolveBudgetNumber} from "../Scenery/@SceneryBudgetConfig.js";

/** Set15 §3.5 — one seam replacing the five `camera as THREE.OrthographicCamera` cast sites
 *  (TerrainRenderer.tsx:1611, SceneryRenderer.tsx:278, IsolineSamplePointsOverlay.tsx:65,
 *  AdaptiveLabel.tsx, PolygonGuide.tsx). Under an ortho camera those read `.left/.right/.top/
 *  .bottom/.zoom`; under a `PerspectiveCamera` they read `undefined` and silently produce NaN —
 *  the class-getters-vs-plain-object silent-false failure mode.
 *
 *  P1 resolved BOTH camera types here so terrain/scenery could render under the perspective 3D
 *  camera while the ortho path stayed byte-identical (the ortho branch is literally the old
 *  arithmetic). P2 added the §3.5 refinement: per-node `worldToScreenAt(x, y)` (constant under
 *  ortho, distance-dependent under perspective) + the frustum-footprint `cameraView`. */

export type ResolveViewInfoResult = {
	/** World rect the camera sees.  Ortho: the visible AABB (pre-load margin separate).
	 *  Persp: a CONSERVATIVE CENTRED SQUARE on the ground plane sized to `maxViewDistance` —
	 *  NOT the frustum footprint.  This is the old P1 shape (used for the solve-footprint
	 *  derivation + the representative `worldToScreen` sample); the REAL frustum footprint is
	 *  `worldViewportBounds` below.  ⚠ For any "what is actually visible" culling use
	 *  `worldViewportBounds` (persp) — `cameraView` here deliberately overshoots. */
	cameraView: Rect;
	/** Visible world rect, expanded by the caller's pre-load margin (ortho only, where the
	 *  constant worldToScreen makes margin meaningful).  Persp: the whole (clamped) footprint is
	 *  the view, so this equals `worldViewportBounds` (NOT `cameraView`). */
	viewportBounds: Rect;
	/** Set15.5 P1 — the REAL ground-projected frustum footprint, clamped to `maxViewDistance` and
	 *  the world bbox (§3.1/§3.3).  Present on the perspective branch only (ortho = undefined,
	 *  since the ortho `viewportBounds` is already exact).  The render quadtree culls + sizes its
	 *  LRU demand from this; it replaces the old world-sized centred-square cull, which is what
	 *  made the demand formula explode under perspective.  `cameraView` is deliberately LEFT as
	 *  the conservative centred square (solve-footprint derivation + representative sample). */
	worldViewportBounds?: Rect;
	/** Pixels per world unit evaluated at a world point (x, y) on the ground plane.  Ortho:
	 *  CONSTANT (the old formula — a perspective-free projection), so split/merge decisions are
	 *  provably identical to the old code (§9 ortho-parity).  Persp: `H / (2·d·tan(θ/2))` where
	 *  `d` is the eye-to-ground distance at that point — larger far away, which is exactly the
	 *  per-node metric the §3.5 refinement requires.  Set15.5 P4 (§6.1): when a `terrainHeightAt`
	 *  sampler is supplied, `d` uses `dz = eyeZ − terrainH` (elevation-aware) so a point on a peak
	 *  near the eye is measured at its true surface distance, not the eye-height distance — only
	 *  refining where terrain is genuinely near the eye. */
	worldToScreenAt(x: number, y: number): number;
	/** Is this a perspective (3D) camera? */
	isPerspective: boolean;
	/** The (validated) max view distance, metres. */
	maxViewDistance: number;
	/** The eye (camera) world position on the ground plane (x, y) + eye height (z).  Ortho: the
	 *  camera centre.  Persp: the eye.  Used to centre the perspective near-field SOLVE footprint. */
	eye: {x: number; y: number; z: number};
};

export const DEFAULT_MAX_VIEW_DISTANCE = 65536 * 2; // full ±65km world extent (D14: terrain to the horizon)

/** Half-extent of the terrain world (±65536 m; D14).  The perspective frustum footprint is
 *  clamped to this so it can never escape the terrain's ±65 km extent. */
const WORLD_HALF = 65536;
/** Set15.5 P1 — modest margin around the ground-projected frustum footprint so a discrete
 *  viewpoint edit (yaw/pitch/fov) doesn't pop the cull boundary.  The 3D camera is locked (no
 *  per-frame panning), so a small relative margin suffices — contrast the ortho `MARGIN = 0.5`
 *  (per-frame panning). */
const PERSPECTIVE_VIEWPORT_MARGIN = 0.2;

/** Resolve an optional `maxViewDistance` knob to a validated positive finite number (§8 R1 —
 *  never a bare comparison against a possibly-absent persisted value). */
export function resolveMaxViewDistance(v: number|n|undefined): number {
	return resolveBudgetNumber(v, DEFAULT_MAX_VIEW_DISTANCE);
}

/** Default scenery draw distance in 3D (metres). Per plan §7 (`sceneryMaxDistance`) — scenery
 *  does not need to reach the 50 km terrain horizon (D14); beyond this, no instances are placed.
 *  Default ~5 km (tunable). */
export const DEFAULT_SCENERY_MAX_DISTANCE = 5000;

/** Resolve the 3D scenery max distance to a validated positive finite number (§8 R1). */
export function resolveSceneryMaxDistance(v: number|n|undefined): number {
	return resolveBudgetNumber(v, DEFAULT_SCENERY_MAX_DISTANCE);
}

/** Default working-grid radius for the perspective SOLVE footprint (metres).  See
 *  `PoissonSolverConfig.mode3D_workingGridRadius` (moved off `MapSettings_View3D` 2026-08-17). */
export const DEFAULT_MODE3D_WORKING_GRID_RADIUS = 1000;

/** Default 3D fine-cell SOLVE size (metres), independent of eye distance.  See
 *  `PoissonSolverConfig.mode3D_fineCellSize` (moved off `MapSettings_View3D` 2026-08-17). */
export const DEFAULT_MODE3D_FINE_CELL_SIZE = 2;

/** Resolve the 3D working-grid solve radius to a validated positive finite number (§8 R1). */
export function resolveMode3DWorkingGridRadius(v: number|n|undefined): number {
	return resolveBudgetNumber(v, DEFAULT_MODE3D_WORKING_GRID_RADIUS);
}

/** Resolve the 3D fine-cell SOLVE size to a validated positive finite number (§8 R1). */
export function resolveMode3DFineCellSize(v: number|n|undefined): number {
	return resolveBudgetNumber(v, DEFAULT_MODE3D_FINE_CELL_SIZE);
}

/** Default render-lattice FLOOR (samples per solved fine-cell edge) — Set15.5 P2 §4. */
export const DEFAULT_RENDER_CHUNK_MAX_SAMPLES_PER_FINE_CELL_EDGE = 2;
/** Set15.5 P2 §4.4 — the floor is the tree's ONLY depth guard, so it must not be disable-able:
 *  a missing/persisted-undefined value must NEVER silently vanish (§8 R1 — a bare `> undefined` is
 *  `false`).  Clamped to `[0.25, 64]`: a value of 0 / ≤0 (which would mean "no floor" = unbounded
 *  subdivision when terrain height approaches eye height, §4.4) is rejected, and an absurdly-large
 *  value is capped.  The clamp ranges intentionally span the useful space: 0.25 → a 128 m leaf at a
 *  2 m solved cell; 64 → a ? m leaf at a 2 m solved cell. */
export const RENDER_CHUNK_SAMPLES_PER_FINE_CELL_EDGE_MIN = 0.25;
export const RENDER_CHUNK_SAMPLES_PER_FINE_CELL_EDGE_MAX = 64;

/** Resolve `renderChunk_maxSamplesPerFineCellEdge` to a validated, clamped positive finite number
 *  (§8 R1 — never a bare comparison against a possibly-absent persisted value).  This knob is the
 *  tree's ONLY depth guard, so unlike a plain budget it must additionally be CLAMPED to `[0.25, 64]`
 *  — 0 / "off" is not permitted (a vanished guard means unbounded subdivision, §4.4). */
export function resolveRenderChunkMaxSamplesPerFineCellEdge(v: number|n|undefined): number {
	const resolved = resolveBudgetNumber(v, DEFAULT_RENDER_CHUNK_MAX_SAMPLES_PER_FINE_CELL_EDGE);
	return Math.min(
		RENDER_CHUNK_SAMPLES_PER_FINE_CELL_EDGE_MAX,
		Math.max(RENDER_CHUNK_SAMPLES_PER_FINE_CELL_EDGE_MIN, resolved),
	);
}

/** Set15.5 P2 §4.5 forward-compat — the FINEST L-band (large-wave, geometry-displacing) wavelength
 *  (metres), or `null` if the L band is empty (all octaves are D).  This is the coarsest* genuine
 *  sub-requested-cell geometric content: octaves with `wl <= maxWavelengthAsDetail` are DETAIL (D,
 *  excluded from `geoHeightGeom` per §1.4), octaves with `wl > maxWavelengthAsDetail` are L (they
 *  DO enter `geoHeightGeom`).  If `maxWavelengthAsDetail` is ever lowered, octaves migrate D→L and
 *  genuinely enter the displacement lattice at wavelengths below the solved cell size — the lattice
 *  floor must then WIDEN to cover them (compute the floor from `min(solvedCell, finestL_wavelength)`)
 *  so it adapts automatically instead of silently clipping newly-promoted L detail.  The finest L
 *  octave is the LAST one still in the L band (wavelengths descend: octave 0 is the largest).
 *  Mirrors the octave iteration in `buildAmbientLDContext` (IsolineEvaluator.ts). */
export function resolveFinestLWavelength(ambient: TerrainAmbient|null|undefined): number|null {
	if (!ambient) return null;
	const octaves = (typeof ambient.octaves == "number" && ambient.octaves > 0) ? ambient.octaves : 0;
	if (octaves <= 0) return null;
	const baseWl = (typeof ambient.wavelength == "number" && ambient.wavelength > 0)
		? ambient.wavelength : null;
	if (baseWl == null) return null;
	const th = ambient.maxWavelengthAsDetail != null && Number.isFinite(ambient.maxWavelengthAsDetail)
		? ambient.maxWavelengthAsDetail
		: baseWl / 2;
	// Iterate octaves from the FINEST upward (largest index first) — the first one still in L wins.
	const lacunarity = (typeof ambient.lacunarity == "number" && ambient.lacunarity > 1) ? ambient.lacunarity : 2;
	for (let i = octaves - 1; i >= 0; i--) {
		const wl = baseWl / Math.pow(lacunarity, i); // octave i's wavelength (mirror buildAmbientLDContext)
		if (wl > th) return wl;
	}
	return null;
}

/** Whether a camera is a perspective camera (vs orthographic).  Three provides no type predicate,
 *  and the R3F default-camera swap means the camera may be either at runtime. */
export function isPerspectiveCamera(camera: THREE.Camera): boolean {
	return (camera as THREE.PerspectiveCamera).isPerspectiveCamera === true;
}

/** The one seam — see the header comment. `size` is the R3F `size` (canvas CSS size in px).
 *
 *  Set15.5 P4 (§6.1) — `terrainHeightAt(x, y)` makes the perspective `worldToScreenAt` ELEVATION-AWARE:
 *  it returns the terrain elevation at a world point, or `null` if not yet evaluated.  The perspective
 *  metric then uses `dz = eyeZ − terrainH` in place of `dz = eyeZ` (§6.1), so a point on a peak ~35 m
 *  below a ~2250 m eye is treated as ~35 m away instead of ~2250 m away.  Passed by the TERRAIN
 *  renderer only (it owns the quadtree whose evaluated chunks back the sampler); the ORTHO branch
 *  ignores it entirely (byte-identical 2D, §9) and the SCENERY renderer omits it (per-tile mpp stays
 *  the eyeZ-only distance — minimal blast radius). */
export function resolveViewInfo(
	camera: THREE.Camera,
	size: {width: number; height: number},
	maxViewDistanceInput: number|n|undefined = undefined,
	terrainHeightAt?: (x: number, y: number)=>number|null,
): ResolveViewInfoResult {
	const maxViewDistance = resolveMaxViewDistance(maxViewDistanceInput);

	if (!isPerspectiveCamera(camera)) {
		const cam = camera as THREE.OrthographicCamera;
		const camViewWidth = (cam.right - cam.left) / cam.zoom;
		const camViewHeight = (cam.top - cam.bottom) / cam.zoom;
		const worldToScreen = camViewWidth > 0 ? size.width / camViewWidth : 1;
		const cameraView = new Rect(
			cam.position.x - camViewWidth / 2,
			cam.position.y - camViewHeight / 2,
			camViewWidth, camViewHeight, false,
		);
		return {
			cameraView,
			viewportBounds: cameraView,
			// Ortho: `worldToScreen` is CONSTANT across the ground plane (no perspective
			// projection), so per-node evaluation is the same constant — byte-identical to the old
			// arithmetic, which is the §9 ortho-parity guarantee.
			worldToScreenAt: ()=>worldToScreen,
			isPerspective: false,
			maxViewDistance,
			eye: {x: cam.position.x, y: cam.position.y, z: cam.position.z},
		};
	}

	// Perspective: the view is locked (§2), so the eye position is static. Derive the on-screen
	// world footprint from the frustum + eye height; the quadtree LRU cap (P1) is sized from it,
	// clamped to maxViewDistance so it cannot explode (§3.5(2) / R1).
	const cam = camera as THREE.PerspectiveCamera;
	// Per-point pixels-per-metre (the §3.5 metric): `H / (2·d·tan(θ/2))` where `d` is the
	// eye-to-ground distance at (x, y).  `cam.position.z` is the eye height above the z=0 ground
	// plane; adding the horizontal offset from the eye gives the true 3D distance to the point.
	// Clamped to a finite floor so a degenerate 0-distance point cannot give Infinity/NaN (R1).
	const fovHalfTan = Math.tan((cam.fov * Math.PI / 180) / 2);
	const eyeX = cam.position.x, eyeY = cam.position.y, eyeZ = cam.position.z;
	const worldToScreenAt = (x: number, y: number): number => {
		const dx = x - eyeX, dy = y - eyeY;
		// Set15.5 P4 (§6.1) — elevation-aware distance: `dz = eyeZ − terrainH` in place of `dz = eyeZ`.
		// The terrain sampler is the quadtree's finest EVALUATED chunk (node-own then ancestor-fallback,
		// never a fresh 0 — §6.2).  If it returns null (no evaluated chunk covers the point yet, i.e.
		// before the bootstrap split→eval pass lands), fall back to `dz = eyeZ` — byte-identical to
		// today, so the metric only REFINES where terrain is genuinely near the eye.  This converges
		// (§6.2 bootstrap: each pass reveals more height, monotonically) rather than looping.
		const terrainH = terrainHeightAt ? terrainHeightAt(x, y) : null;
		const dz = terrainH != null ? eyeZ - terrainH : eyeZ;
		const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
		return size.height / (2 * Math.max(1e-3, d) * fovHalfTan);
	};

	// Set15.5 P1 — the REAL ground-projected frustum footprint for the QUADTREE cull + LRU demand
	// formula.  Under perspective, `cameraView` stays the conservative centred square (it is used
	// by the SOLVE footprint derivation + the representative `worldToScreen` sample, which should
	// stay shape-independent); this NEW `viewportBounds` is what actually bounds the render quadtree
	// (§3.1).  It is the ground-plane projection of the camera's view frustum corners, so the tree
	// refines only the ~2.6 km footprint the eye can actually see (at pitch ~90° from ~2250 m),
	// rather than the whole ±65 km world — a ~10× leaf reduction (plan §3.2).
	//
	// The frustum's four corners are computed from the camera's inverse view-projection matrix at
	// the near plane's ground-projected depth, with the horizontal FOV aspect taken from the
	// canvas size (the camera's own aspect is unreliable here).  The ground-plane footprint of the
	// camera's view volume is the intersection of its four frustum side planes with z = 0 — which,
	// for an upright camera, is exactly the convex hull of the four corners where the NEAR-plane
	// frustum edges (shot from the eye through the near-plane corners) pierce the ground plane.
	// Clamped to `maxViewDistance` (D14: terrain to the horizon) + the world bbox ±WORLD_HALF, and
	// expanded by a modest margin so a viewpoint edit doesn't pop (§3.3).
	const worldViewportBounds = resolvePerspectiveViewportBounds(
		cam, size, {x: eyeX, y: eyeY, z: eyeZ}, maxViewDistance,
	);

	// `cameraView` stays the CONSERVATIVE CENTRED SQUARE sized to `maxViewDistance` — the shape
	// the SOLVE footprint + the representative `worldToScreen` sample rely on (the per-node metric
	// `worldToScreenAt` is the real refinement; the footprint's exact shape is refined further by
	// fog).  Set15.5 P1 keeps it unchanged so `nextCameraView` (solve) and the eye-centred sample
	// are byte-identical; only `viewportBounds` (the render quadtree cull) becomes the real
	// footprint.
	const half = Math.min(Math.max(maxViewDistance, 1e-3) / 2, WORLD_HALF);
	const cx = Math.min(Math.max(eyeX, -WORLD_HALF), WORLD_HALF);
	const cy = Math.min(Math.max(eyeY, -WORLD_HALF), WORLD_HALF);
	const cameraView = new Rect(cx - half, cy - half, half * 2, half * 2, false);
	return {
		cameraView,
		viewportBounds: worldViewportBounds,
		worldToScreenAt,
		isPerspective: true,
		maxViewDistance,
		eye: {x: eyeX, y: eyeY, z: eyeZ},
		worldViewportBounds,
	};
}

/** Build the `PerspectiveCamera` from a `Mod_Viewpoint` + its world eye position. Shared by
 *  `MapSurface`'s `<Canvas key={mode}>` camera prop and any P1 render helpers. */
export function buildPerspectiveCamera(
	eye: {x: number; y: number; z: number},
	opts: {yaw: number; pitch: number; fov: number; roll?: number},
	aspect = 1,
): THREE.PerspectiveCamera {
	// `aspect` = canvas width/height (CSS px).  The mode-switch `<Canvas key>` remount creates a
	// FRESH camera each time, and R3F only re-applies the canvas aspect via its own size-change
	// subscriber (`updateCamera`) — which can race/never run for that fresh camera.  So we build
	// with the right aspect here AND the ViewpointCameraUpdater re-applies `useThree().size` every
	// frame (defense in depth): a stale `1` on a wide canvas stretches ground circles into wide
	// ellipses (a ~1600×1100 mountaintop instead of a circle).
	const cam = new THREE.PerspectiveCamera(opts.fov, aspect, 0.1, 100000);
	cam.position.set(eye.x, eye.y, eye.z);
	// Coordinate convention (matches `Mod_Viewpoint`): yaw measured from map +y ("north"/forward),
	// 0 = looking toward +y; pitch >0 = looking down toward terrain; roll about the view axis.
	// CRITICAL: this app maps world z+ = "up" (ResolveObjPosInWorld: x+ right, y+ forward, z+ up),
	// but three's camera defaults to up = (0,1,0) (three y+ = up). Without overriding it, lookAt
	// rolls the camera so its screen-up points along the app's "north" instead of its vertical —
	// a 90° side-tip. Setting up = +z keeps the camera upright (roll 0 = level horizon).
	cam.up.set(0, 0, 1);
	const yawRad = opts.yaw * Math.PI / 180;
	const pitchRad = opts.pitch * Math.PI / 180;
	// Forward on the ground plane (yaw from +y, same as ViewpointOverlay's wedge).
	const fx = Math.sin(yawRad), fy = Math.cos(yawRad);
	// Target = forward, pitched DOWN by `pitch`, at a large distance so the camera looks at the
	// ground ahead (consistent with the 2D wedge's ground reach).
	const reach = eye.z / Math.max(1e-3, Math.tan(pitchRad));
	const targetX = eye.x + fx * reach;
	const targetY = eye.y + fy * reach;
	cam.lookAt(targetX, targetY, 0);
	// Apply roll (rotation about the view axis). lookAt already orients the camera with up = +z;
	// rotateZ spins about the camera's local z = the view axis, which is exactly a roll.
	if (opts.roll) cam.rotateZ(opts.roll * Math.PI / 180);
	cam.updateProjectionMatrix();
	return cam;
}

/** Compute the eye (world XYZ) for a viewpoint given its anchor XY and the BAKED absolute eye
 *  height (metres above world z=0).  Set15 P5: the eye z is the baked `eyeHeight` directly —
 *  NOT terrain-relative — so the camera position is stable and never churns when the terrain
 *  height lands/updates.  (The terrain-relative "above terrain" value is a UI-only target baked
 *  into `eyeHeight` via the Viewpoint UI's Apply button.) */
export function viewpointEyeFromAnchor(anchor: Vec3, eyeHeight: number): Vec3 {
	const h = typeof eyeHeight == "number" && Number.isFinite(eyeHeight) ? eyeHeight : 0;
	return new Vec3(anchor.x, anchor.y, h);
}

/** Set15.5 P1 (§3.1/§3.3) — the ground-projected frustum footprint of a `PerspectiveCamera`.
 *
 *  Returns the world AABB that the camera can actually see on the ground plane (z = 0), expanded
 *  by a small relative margin and clamped to `maxViewDistance` + the world bbox.  This is what the
 *  render quadtree culls against — replacing the old world-sized centred-square `viewportBounds`
 *  that under perspective made the tree refine the entire ±65 km horizon even though the visible
 *  footprint at pitch ~90° is only ~2.6 km tall (§1.1/§3.1).
 *
 *  Method: for an upright camera, the ground footprint of the view volume is the convex hull of
 *  the four points where rays from the eye through the NEAR-plane corners pierce z = 0.  Rather
 *  than reconstruct those rays from yaw/pitch/fov by hand (fragile), we take the camera's inverse
 *  view-projection matrix — which maps NDC (−1..1)³ at the near plane to world space — and
 *  intersect each of the four NDC corners with the ground plane, then clamp.  This is exact for
 *  an upright camera and stays correct through yaw/pitch/fov edits.
 *
 *  @param margin  relative expansion (0 = exact footprint).  Set15.5 default 0.2 (§3.3).
 *  @returns a finite, clamped AABB (never NaN/Infinity — R1). */
export function resolvePerspectiveViewportBounds(
	cam: THREE.PerspectiveCamera,
	size: {width: number; height: number},
	eye: {x: number; y: number; z: number},
	maxViewDistance: number,
	margin = PERSPECTIVE_VIEWPORT_MARGIN,
): Rect {
	// Aspect from the actual canvas size (the camera's own aspect may lag the first R3F render).
	const aspect = size.height > 0 ? size.width / size.height : 1;
	// Effective vertical FOV (the camera's fov is the vertical fov in three.js); horizontal
	// half-angle = atan(tan(vfov/2) · aspect).
	const vfovHalfTan = Math.tan((cam.fov * Math.PI / 180) / 2);
	const hfovHalfTan = vfovHalfTan * aspect;
	// Ensure `cam.matrixWorld` reflects the current orientation (a freshly-`lookAt`ed camera —
	// e.g. in a test — has a stale identity matrixWorld until updateMatrixWorld/updateMatrix runs).
	cam.updateMatrixWorld();
	// Ground reach: how far the eye's cone reaches along the ground.  Pitch 0 (horizontal) →
	// the cone is parallel to the ground and never hits it (clamped by maxViewDistance instead).
	// We derive the camera basis from `cam.matrixWorld` (already the world orientation from
	// lookAt) rather than reconstructing yaw/pitch by hand — robust through any euler order.
	// Camera space (three convention): forward = -Z, right = +X, up = +Y.
	const camWorld = cam.matrixWorld;
	const right = new THREE.Vector3().setFromMatrixColumn(camWorld, 0).normalize();
	const camUpV = new THREE.Vector3().setFromMatrixColumn(camWorld, 1).normalize();
	const fwd = new THREE.Vector3().setFromMatrixColumn(camWorld, 2).negate().normalize();
	// Ray from the eye toward each near-plane corner: basis direction = fwd + tanH·right ±
	// tanV·up, normalised (direction is all that matters for the ground intersection).
	const tanV = vfovHalfTan;
	const tanH = hfovHalfTan;
	// Ray from the eye toward each near-plane corner: eyeZ basis direction = fwd + tanH·right ±
	// tanV·up, normalised (direction is all that matters for the ground intersection).
	const eyePos = new THREE.Vector3(eye.x, eye.y, eye.z);
	const dirs = [
		new THREE.Vector3().addVectors(right.clone().multiplyScalar(tanH), camUpV.clone().multiplyScalar(tanV)).add(fwd),
		new THREE.Vector3().addVectors(right.clone().multiplyScalar(-tanH), camUpV.clone().multiplyScalar(tanV)).add(fwd),
		new THREE.Vector3().addVectors(right.clone().multiplyScalar(tanH), camUpV.clone().multiplyScalar(-tanV)).add(fwd),
		new THREE.Vector3().addVectors(right.clone().multiplyScalar(-tanH), camUpV.clone().multiplyScalar(-tanV)).add(fwd),
	];
	let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
	let anyGroundHit = false;
	const skywardDirs: THREE.Vector3[] = [];
	for (const dir of dirs) {
		dir.normalize();
		// Ground intersection: eye + t·dir with z=0 ⇒ t = −eyeZ / dir.z.
		if (dir.z > -1e-6) { skywardDirs.push(dir); continue; }
		const t = -eyePos.z / dir.z;
		if (!Number.isFinite(t) || t < 0) continue;
		const p = new THREE.Vector3().copy(eyePos).addScaledVector(dir, t);
		if (p.x < minX) minX = p.x;
		if (p.y < minY) minY = p.y;
		if (p.x > maxX) maxX = p.x;
		if (p.y > maxY) maxY = p.y;
		anyGroundHit = true;
	}
	// When the frustum hits the ground but a corner ray points skyward (pitch < vfov/2: the camera
	// looks up over a TOP corner), extend it horizontally to maxViewDistance (D14) — otherwise the
	// footprint collapses to only the steep near-eye bottom corners and culls everything (incl. centre).
	if (anyGroundHit) {
		for (const dir of skywardDirs) {
			const horiz = new THREE.Vector3(dir.x, dir.y, 0);
			const horizLen = horiz.length();
			if (horizLen < 1e-9) continue; // straight up: no lateral reach → contributes nothing
			horiz.multiplyScalar(maxViewDistance / horizLen);
			const p = new THREE.Vector3().copy(eyePos).add(horiz);
			if (p.x < minX) minX = p.x;
			if (p.y < minY) minY = p.y;
			if (p.x > maxX) maxX = p.x;
			if (p.y > maxY) maxY = p.y;
		}
	}
	// Degenerate / no-ground-hit fallback: a level pitch looks at the horizon and its cone never
	// pierces the ground ahead of it — the visible footprint is bounded by `maxViewDistance`
	// (D14), not the frustum.  Fall back to a centred square of side maxViewDistance (the old P1
	// shape) so the cull is still bounded.
	if (!anyGroundHit || !Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
		const half = Math.min(Math.max(maxViewDistance, 1e-3) / 2, WORLD_HALF);
		return new Rect(eye.x - half, eye.y - half, half * 2, half * 2, false);
	}
	// Clamp to the world bbox ±WORLD_HALF.
	minX = Math.max(minX, -WORLD_HALF); maxX = Math.min(maxX, WORLD_HALF);
	minY = Math.max(minY, -WORLD_HALF); maxY = Math.min(maxY, WORLD_HALF);
	const w = maxX - minX, h = maxY - minY;
	// Expand by the margin about the footprint centre (keeps it clamped: recompute centre after
	// clamping, then expand, then re-clamp).
	let cxm = (minX + maxX) / 2, cym = (minY + maxY) / 2;
	let wm = w * (1 + margin * 2), hm = h * (1 + margin * 2);
	// Re-clamp after the margin so the rect stays within the world.
	wm = Math.min(wm, WORLD_HALF * 2);
	hm = Math.min(hm, WORLD_HALF * 2);
	return new Rect(
		Math.max(cxm - wm / 2, -WORLD_HALF),
		Math.max(cym - hm / 2, -WORLD_HALF),
		wm, hm, false,
	);
}

